import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { SECOND } from '~/constants';

interface UseTextureLoadMonitorProps {
  scene: THREE.Group | undefined;
  onLoadError: () => void;
  timeout?: number;
}

export default function useTextureLoadMonitor({
  scene,
  onLoadError,
  timeout = 100 * SECOND,
}: UseTextureLoadMonitorProps) {
  const processedSceneRef = useRef<THREE.Group | null>(null);

  useEffect(() => {
    if (!scene) return;

    if (processedSceneRef.current === scene) return;

    processedSceneRef.current = scene;

    let totalTextureCount = 0;
    let loadedTextureCount = 0;
    const startTime = performance.now();
    const textureLoadTimes = new Map();
    let hasLoggedCompletion = false;

    const textureLoadTimeout = setTimeout(() => {
      if (loadedTextureCount < totalTextureCount) {
        console.error('텍스처 로딩 시간 초과: 100초 이상 소요됨');
        onLoadError();
      }
    }, timeout);

    scene.traverse((x) => {
      if (
        x instanceof THREE.Mesh &&
        x.material instanceof THREE.MeshStandardMaterial
      ) {
        // 텍스처 카운팅
        const maps = [
          { map: x.material.map, name: 'map' },
          { map: x.material.normalMap, name: 'normalMap' },
          { map: x.material.roughnessMap, name: 'roughnessMap' },
          { map: x.material.metalnessMap, name: 'metalnessMap' },
          { map: x.material.emissiveMap, name: 'emissiveMap' },
          { map: x.material.aoMap, name: 'aoMap' },
          { map: x.material.displacementMap, name: 'displacementMap' },
          { map: x.material.alphaMap, name: 'alphaMap' },
          { map: x.material.lightMap, name: 'lightMap' },
          { map: x.material.envMap, name: 'envMap' },
        ];

        maps.forEach(({ map, name }) => {
          if (map) {
            const textureKey = `${
              x.name || 'unnamed'
            }_${name}_${totalTextureCount}`;
            totalTextureCount++;

            if (
              map.image &&
              map.image instanceof HTMLImageElement &&
              !map.image.complete
            ) {
              map.image.addEventListener('load', () => {
                const loadTime = performance.now() - startTime;
                textureLoadTimes.set(textureKey, loadTime);
                loadedTextureCount++;

                // 모든 텍스처가 로드되었는지 확인
                if (
                  loadedTextureCount === totalTextureCount &&
                  !hasLoggedCompletion
                ) {
                  hasLoggedCompletion = true;
                  const totalLoadTime = performance.now() - startTime;
                  console.log(
                    `모든 텍스처 로드 완료: ${loadedTextureCount}개, 총 ${totalLoadTime.toFixed(
                      2,
                    )}ms 소요`,
                  );
                }
              });
            } else {
              // 이미 로드된 텍스처
              const loadTime = performance.now() - startTime;
              textureLoadTimes.set(textureKey, loadTime);
              loadedTextureCount++;
            }
          }
        });
      } else if (x instanceof THREE.Mesh && Array.isArray(x.material)) {
        // 다중 재질 메시 처리
        x.material.forEach((mat, matIndex) => {
          if (mat instanceof THREE.MeshStandardMaterial) {
            // 텍스처 카운팅
            const maps = [
              { map: mat.map, name: 'map' },
              { map: mat.normalMap, name: 'normalMap' },
              { map: mat.roughnessMap, name: 'roughnessMap' },
              { map: mat.metalnessMap, name: 'metalnessMap' },
              { map: mat.emissiveMap, name: 'emissiveMap' },
              { map: mat.aoMap, name: 'aoMap' },
              { map: mat.displacementMap, name: 'displacementMap' },
              { map: mat.alphaMap, name: 'alphaMap' },
              { map: mat.lightMap, name: 'lightMap' },
              { map: mat.envMap, name: 'envMap' },
            ];

            maps.forEach(({ map, name }) => {
              if (map) {
                const textureKey = `${
                  x.name || 'unnamed'
                }_mat${matIndex}_${name}_${totalTextureCount}`;
                totalTextureCount++;

                if (
                  map.image &&
                  map.image instanceof HTMLImageElement &&
                  !map.image.complete
                ) {
                  map.image.addEventListener('load', () => {
                    const loadTime = performance.now() - startTime;
                    textureLoadTimes.set(textureKey, loadTime);
                    loadedTextureCount++;

                    // 모든 텍스처가 로드되었는지 확인
                    if (
                      loadedTextureCount === totalTextureCount &&
                      !hasLoggedCompletion
                    ) {
                      hasLoggedCompletion = true;
                      const totalLoadTime = performance.now() - startTime;
                      console.log(
                        `모든 텍스처 로드 완료: ${loadedTextureCount}개, 총 ${totalLoadTime.toFixed(
                          2,
                        )}ms 소요`,
                      );
                    }
                  });
                } else {
                  // 이미 로드된 텍스처
                  const loadTime = performance.now() - startTime;
                  textureLoadTimes.set(textureKey, loadTime);
                  loadedTextureCount++;
                }
              }
            });
          }
        });
      }
    });

    // 이미 모든 텍스처가 로드된 경우 (이미 캐시된 경우)
    if (
      loadedTextureCount === totalTextureCount &&
      totalTextureCount > 0 &&
      !hasLoggedCompletion
    ) {
      hasLoggedCompletion = true;
      const totalLoadTime = performance.now() - startTime;
      console.log(
        `모든 텍스처 즉시 로드 완료: ${loadedTextureCount}개, 총 ${totalLoadTime.toFixed(
          2,
        )}ms 소요`,
      );
    }

    return () => {
      clearTimeout(textureLoadTimeout);
    };
  }, [scene, onLoadError, timeout]);
}
